/**
 * SSE Event Models
 * 
 * Type definitions for Server-Sent Events from monitor-backend
 */

/**
 * Connection status types
 */
export type ConnectionStatus = 'connected' | 'connecting' | 'disconnected' | 'error';

/**
 * SSE Event Types from the backend
 */
export enum SSEEventType {
  STATUS_UPDATE = 'status_update',
  ALERT = 'alert',
  NOTIFICATION_SEEN = 'notification_seen',
  DEVICE_LOG = 'device_log',
  SCREENSHOT_POOL_UPDATED = 'screenshot_pool_updated',
}

/**
 * VEO SSE Event Types (organization-scoped)
 */
export enum VeoSSEEventType {
  DEVICE_STATUS = 'device_status',
  LOCATION_UPDATE = 'location_update',
  ROUTE_ASSIGNED = 'route_assigned',
  DASHBOARD_REFRESH = 'dashboard_refresh',
  VEO_NOTIFICATION = 'veo_notification',
  VEO_NOTIFICATION_SEEN = 'veo_notification_seen',
}

/**
 * VEO SSE Connection Info (sent on connect)
 */
export interface VeoSSEConnectedEvent {
  status: 'connected';
  organizations: number[];
  eventTypes: string[];
}

/**
 * VEO Device Status Change Event
 */
export interface VeoDeviceStatusEvent {
  device_serial: string;
  old_status: string;
  new_status: string;
  changed_at: string;
  organization_id?: number;
}

/**
 * VEO Location Update Event
 */
export interface VeoLocationUpdateEvent {
  location_id?: number;
  device_serial?: string;
  updates?: string[];
  screen_type_properties_id?: number | null;
  organization_ids: number[];
  changed_at: string;
}

/**
 * VEO Route Assignment Event
 */
export interface VeoRouteAssignedEvent {
  location_id: number;
  route_name: string;
  organization_ids: number[];
  changed_at: string;
  device_serial?: string;
}

/**
 * VEO Dashboard Refresh Event
 */
export interface VeoDashboardRefreshEvent {
  type: string;
  date: string;
  organization_ids: number[];
  processed?: number;
  changed_at: string;
}

/**
 * Device Status Change Event
 */
export interface DeviceStatusEvent {
  device_serial: string;
  old_status: string;
  new_status: string;
  changed_at: string;
}

/**
 * Alert/Notification Event
 */
export interface AlertEvent {
  id: string;
  device_id: number;
  device_serial: string;
  device_name?: string;
  message: string;
  status: 'info' | 'warning' | 'error' | 'success';
  type: string;
  created_at: string;
  timestamp: string;
  seen: boolean;
}

/**
 * Notification Seen Event
 */
export interface NotificationSeenEvent {
  id: string;
  seen_at: string;
}

/**
 * Device Log Event
 */
export interface DeviceLogEvent {
  device_id: number;
  device_serial: string;
  level: 'info' | 'warn' | 'error';
  message: string;
  timestamp: string;
}

/**
 * Screenshot Pool Updated Event
 */
export interface ScreenshotPoolEvent {
  device_id: number;
  device_serial: string;
  url: string;
  timestamp: string;
}
